import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { defaultSettings, OrganisationSettings } from '@omnea-app/org-settings/defaultSettings'
import { UpdateOrganisationSettingRequest } from '@omnea-app/org-settings/orgSettingsFrontendClient'
import { OrgSettingsHttpClient } from '@omnea-app/org-settings/orgSettingsHttpClient'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'

export const GetOrgSettingsQueryKey = 'GetOrgSettingsQueryKey'

type PollWhile = (data: OrganisationSettings | undefined) => boolean

export const useOrganisationSettingsByName = <T extends keyof OrganisationSettings>(
  settingName: T,
  pollWhile?: PollWhile
): { organisationSetting: OrganisationSettings[T]; isLoading: boolean } => {
  const { data, isLoading } = useOrganisationSettings(pollWhile)
  const setting = data?.[settingName]

  if (settingName === 'fallbackAssignee' && !Array.isArray(setting)) {
    return { organisationSetting: defaultSettings[settingName], isLoading }
  }

  return { organisationSetting: setting ?? defaultSettings[settingName], isLoading }
}

const sixHoursMs = 6 * 60 * 60 * 1000
export const useOrganisationSettings = (pollWhile?: PollWhile) => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: [GetOrgSettingsQueryKey],
    queryFn: async () => {
      const token = await getToken()
      const orgSettingsClient = new OrgSettingsHttpClient({ authorization: token })
      const { data, error } = await orgSettingsClient.GET('/organisation', {})
      throwIfApiError(error)
      return data as OrganisationSettings
    },
    refetchInterval: (data) => {
      if (pollWhile && pollWhile(data.state.data) === true) {
        return 3000
      }
      return false
    },
    staleTime: sixHoursMs,
  })
}

export const useOrganisationSettingsByNameSuspense = <T extends keyof OrganisationSettings>(settingName: T) => {
  const { getToken } = useAuthenticatedUser()
  return useSuspenseQuery({
    queryKey: [GetOrgSettingsQueryKey],
    queryFn: async () => {
      const token = await getToken()
      const orgSettingsClient = new OrgSettingsHttpClient({ authorization: token })
      const { data, error } = await orgSettingsClient.GET('/organisation', {})
      throwIfApiError(error)
      return data
    },
    select: (data) => data[settingName] ?? defaultSettings[settingName],
    staleTime: sixHoursMs,
  })
}

type UpdateOrganisationSettingProps = {
  settingName: keyof OrganisationSettings
  settingValue: OrganisationSettings[keyof OrganisationSettings]
}

export const useUpdateOrganisationSetting = () => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation('settings', { keyPrefix: 'organisation-settings' })

  return useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [GetOrgSettingsQueryKey] })
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: [GetOrgSettingsQueryKey] })
      enqueueSnackbar(t('update-error'), { variant: 'error' })
    },
    mutationFn: async ({ settingName, settingValue }: UpdateOrganisationSettingProps) => {
      const token = await getToken()
      const orgSettingsClient = new OrgSettingsHttpClient({ authorization: token })
      const { data, error } = await orgSettingsClient.POST('/organisation/update', {
        body: { name: settingName, value: settingValue } as UpdateOrganisationSettingRequest,
      })
      throwIfApiError(error)
      return data as OrganisationSettings
    },
  })
}
